'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Zap,
  Save,
  Play,
  Eye,
  ChevronDown,
  ChevronUp,
  Workflow,
  RefreshCw,
} from 'lucide-react';
import { useThemeStore } from '../../store/themeStore';
import type {
  Workflow as WorkflowType,
  Trigger,
  Condition,
  Action,
  WorkflowCategory,
} from './automationTypes';
import { getCategoryConfig } from './automationConstants';
import { automationAPI } from './automationAPI';
import { TriggerConfigurator, ConditionBuilder, ActionBuilder } from './WorkflowBuilderComponents';

const WorkflowBuilderPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useThemeStore();
  const pc = theme.hex.primary;
  const navigate = useNavigate();
  const { workflowId } = useParams<{ workflowId: string }>();
  const queryClient = useQueryClient();
  const isEditing = Boolean(workflowId);

  const categoryConfig = useMemo(() => getCategoryConfig(t), [t]);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<WorkflowCategory>('engagement');
  const [trigger, setTrigger] = useState<Trigger | null>(null);
  const [conditions, setConditions] = useState<Condition[]>([]);
  const [actions, setActions] = useState<Action[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_editingAction, setEditingAction] = useState<Action | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Section expand/collapse
  const [expandedSections, setExpandedSections] = useState({
    details: true,
    trigger: true,
    conditions: true,
    actions: true,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Load existing workflow if editing
  const { data: existingWorkflow, isLoading: loadingWorkflow } = useQuery({
    queryKey: ['workflow', workflowId],
    queryFn: async () => {
      if (!workflowId) {
        return null;
      }
      const res = await automationAPI.getWorkflows();
      const items = res.data?.workflows ?? res.data ?? [];
      const list = Array.isArray(items) ? items : [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const found = list.find((w: any) => w.id === workflowId || w._id === workflowId);
      return found || null;
    },
    enabled: Boolean(workflowId),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (existingWorkflow) {
      setName(existingWorkflow.name || '');
      setDescription(existingWorkflow.description || '');
      setCategory(existingWorkflow.category || 'engagement');
      setTrigger(existingWorkflow.trigger || null);
      setConditions(existingWorkflow.conditions || []);
      setActions(existingWorkflow.actions || []);
    }
  }, [existingWorkflow]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: Omit<WorkflowType, 'id' | 'createdAt' | 'updatedAt' | 'stats'>) =>
      automationAPI.createWorkflow(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      toast.success(t('auto_created', 'Workflow created successfully'));
      navigate('/automations');
    },
    onError: () => {
      toast.error(t('auto_create_failed', 'Failed to create workflow'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: { id: string; updates: Partial<WorkflowType> }) =>
      automationAPI.updateWorkflow(data.id, data.updates).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      toast.success(t('auto_updated', 'Workflow updated successfully'));
      navigate('/automations');
    },
    onError: () => {
      toast.error(t('auto_update_failed', 'Failed to update workflow'));
    },
  });

  const testMutation = useMutation({
    mutationFn: (id: string) => automationAPI.testWorkflow(id),
    onSuccess: () => {
      toast.success(t('auto_test_triggered', 'Workflow test triggered'));
    },
    onError: () => {
      toast.error(t('auto_test_failed', 'Failed to test workflow'));
    },
  });

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error(t('auto_name_required', 'Workflow name is required'));
      return;
    }
    if (!trigger) {
      toast.error(t('auto_trigger_required', 'Trigger is required'));
      return;
    }
    if (actions.length === 0) {
      toast.error(t('auto_action_required', 'At least one action is required'));
      return;
    }

    setIsSaving(true);
    try {
      const workflowData = {
        name,
        description,
        category,
        trigger,
        conditions,
        actions,
        status: 'draft' as const,
      };

      if (isEditing && workflowId) {
        await updateMutation.mutateAsync({ id: workflowId, updates: workflowData });
      } else {
        await createMutation.mutateAsync(workflowData);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAsDraft = async () => {
    if (!name.trim()) {
      toast.error(t('auto_name_required', 'Workflow name is required'));
      return;
    }

    setIsSaving(true);
    try {
      const workflowData = {
        name,
        description,
        category,
        trigger: trigger || { type: 'manual' as const, config: {} },
        conditions,
        actions,
        status: 'draft' as const,
      };

      if (isEditing && workflowId) {
        await updateMutation.mutateAsync({ id: workflowId, updates: workflowData });
      } else {
        await createMutation.mutateAsync(workflowData);
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing && loadingWorkflow) {
    return (
      <div className="bg-gray-50 min-h-full flex items-center justify-center">
        <div className="text-center">
          <RefreshCw size={24} className="text-emerald-400 animate-spin mx-auto mb-3" />
          <p className="text-gray-500 text-sm">{t('auto_loading_wf', 'Loading workflow...')}</p>
        </div>
      </div>
    );
  }

  // Count configured items for summary badges
  const triggerConfigured = Boolean(trigger);
  const conditionCount = conditions.length;
  const actionCount = actions.length;

  return (
    <div className="bg-gray-50 min-h-full">
      {/* Top Bar */}
      <div className="bg-white border-b border-slate-200/60" style={{ boxShadow: '0 1px 3px 0 rgba(0,0,0,0.04), 0 1px 2px -1px rgba(0,0,0,0.03)' }}>
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/automations')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600 hover:text-gray-900"
              aria-label={t('auto_back', 'Back to automations')}
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              <Zap size={22} className="text-emerald-600" />
              <h1 className="text-xl font-bold text-gray-900">
                {isEditing ? t('auto_edit_wf', 'Edit Workflow') : t('auto_create_new_wf', 'Create New Workflow')}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Summary badges */}
            <div className="hidden md:flex items-center gap-2 mr-2">
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${triggerConfigured ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                {triggerConfigured ? t('auto_trigger_set', 'Trigger set') : t('auto_no_trigger', 'No trigger')}
              </span>
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${conditionCount > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                {conditionCount} {conditionCount === 1 ? t('auto_condition_singular', 'condition') : t('auto_conditions_label', 'conditions')}
              </span>
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${actionCount > 0 ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'}`}>
                {actionCount} {actionCount === 1 ? t('auto_action_singular', 'action') : t('auto_actions_label', 'actions')}
              </span>
            </div>

            {isEditing && workflowId && (
              <button
                onClick={() => testMutation.mutate(workflowId)}
                disabled={testMutation.isPending}
                className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <Play size={14} />
                {testMutation.isPending ? t('auto_testing', 'Testing...') : t('auto_test', 'Test')}
              </button>
            )}

            <button
              onClick={handleSaveAsDraft}
              disabled={isSaving}
              className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <Eye size={14} />
              {t('auto_save_draft', 'Save as Draft')}
            </button>

            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-1.5 px-5 py-2 text-white font-semibold text-sm rounded-lg transition-colors disabled:opacity-50"
              style={{ backgroundColor: pc, opacity: 1 }}
              onMouseEnter={(e) => !isSaving && (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            >
              <Save size={14} />
              {isSaving ? t('auto_saving', 'Saving...') : t('auto_save_wf', 'Save Workflow')}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Left Column - Workflow Details */}
          <div className="lg:col-span-1 space-y-4">

            {/* Details Card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl border border-slate-200/60 overflow-hidden"
              style={{ boxShadow: '0 1px 3px 0 rgba(0,0,0,0.04), 0 1px 2px -1px rgba(0,0,0,0.03)' }}
            >
              <button
                onClick={() => toggleSection('details')}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Workflow size={16} className="text-emerald-600" />
                  <h2 className="text-sm font-bold text-gray-900">{t('auto_wf_details', 'Workflow Details')}</h2>
                </div>
                {expandedSections.details ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
              </button>

              {expandedSections.details && (
                <div className="px-5 pb-5 space-y-4 border-t border-gray-100">
                  <div className="pt-4">
                    <label htmlFor="wf-name" className="block text-sm font-semibold text-gray-900 mb-2">
                      {t('auto_wf_name', 'Workflow Name')}
                    </label>
                    <input
                      id="wf-name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 transition-all duration-200 bg-white text-sm"
                      placeholder={t('auto_wf_name_hint', 'e.g., Auto-Reply to Comments')}
                    />
                  </div>

                  <div>
                    <label htmlFor="wf-description" className="block text-sm font-semibold text-gray-900 mb-2">
                      {t('auto_description', 'Description')}
                    </label>
                    <textarea
                      id="wf-description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 transition-all duration-200 bg-white text-sm"
                      placeholder={t('auto_wf_desc_hint', 'Describe what this workflow does')}
                      rows={3}
                    />
                  </div>

                  <div>
                    <label htmlFor="wf-category" className="block text-sm font-semibold text-gray-900 mb-2">
                      {t('auto_category', 'Category')}
                    </label>
                    <select
                      id="wf-category"
                      value={category}
                      onChange={(e) => setCategory(e.target.value as WorkflowCategory)}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 transition-all duration-200 bg-white text-sm"
                    >
                      {Object.entries(categoryConfig).map(([key, config]) => (
                        <option key={key} value={key}>
                          {config.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Category Preview */}
                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <div className={`${categoryConfig[category].color} text-white rounded p-1.5`}>
                      {categoryConfig[category].icon}
                    </div>
                    <span className="text-sm font-medium text-gray-700">{categoryConfig[category].label}</span>
                  </div>
                </div>
              )}
            </motion.div>

            {/* Quick Help Card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl border border-slate-200/60 p-5"
              style={{ boxShadow: '0 1px 3px 0 rgba(0,0,0,0.04), 0 1px 2px -1px rgba(0,0,0,0.03)' }}
            >
              <h3 className="text-sm font-bold text-gray-900 mb-3">{t('auto_quick_help', 'Quick Guide')}</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</div>
                  <p className="text-xs text-gray-600">{t('auto_help_step1', 'Set up a trigger to define when the workflow starts')}</p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</div>
                  <p className="text-xs text-gray-600">{t('auto_help_step2', 'Add conditions to filter when the workflow should run (optional)')}</p>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</div>
                  <p className="text-xs text-gray-600">{t('auto_help_step3', 'Add one or more actions that execute when triggered')}</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right Column - Workflow Configuration */}
          <div className="lg:col-span-2 space-y-4">

            {/* Trigger Card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="bg-white rounded-xl border border-slate-200/60 overflow-hidden"
              style={{ boxShadow: '0 1px 3px 0 rgba(0,0,0,0.04), 0 1px 2px -1px rgba(0,0,0,0.03)' }}
            >
              <button
                onClick={() => toggleSection('trigger')}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Zap size={16} className="text-emerald-600" />
                  <h2 className="text-sm font-bold text-gray-900">{t('auto_trigger', 'Trigger')}</h2>
                  {triggerConfigured && (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                      {t('auto_configured', 'Configured')}
                    </span>
                  )}
                </div>
                {expandedSections.trigger ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
              </button>

              {expandedSections.trigger && (
                <div className="px-5 pb-5 border-t border-gray-100 pt-4">
                  <TriggerConfigurator trigger={trigger} onChange={setTrigger} />
                </div>
              )}
            </motion.div>

            {/* Conditions Card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl border border-slate-200/60 overflow-hidden"
              style={{ boxShadow: '0 1px 3px 0 rgba(0,0,0,0.04), 0 1px 2px -1px rgba(0,0,0,0.03)' }}
            >
              <button
                onClick={() => toggleSection('conditions')}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Zap size={16} className="text-blue-600" />
                  <h2 className="text-sm font-bold text-gray-900">{t('auto_conditions', 'Conditions (Optional)')}</h2>
                  {conditionCount > 0 && (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                      {conditionCount}
                    </span>
                  )}
                </div>
                {expandedSections.conditions ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
              </button>

              {expandedSections.conditions && (
                <div className="px-5 pb-5 border-t border-gray-100 pt-4">
                  <ConditionBuilder conditions={conditions} onChange={setConditions} />
                </div>
              )}
            </motion.div>

            {/* Actions Card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-white rounded-xl border border-slate-200/60 overflow-hidden"
              style={{ boxShadow: '0 1px 3px 0 rgba(0,0,0,0.04), 0 1px 2px -1px rgba(0,0,0,0.03)' }}
            >
              <button
                onClick={() => toggleSection('actions')}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Zap size={16} className="text-purple-600" />
                  <h2 className="text-sm font-bold text-gray-900">{t('auto_actions', 'Actions')}</h2>
                  {actionCount > 0 && (
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                      {actionCount}
                    </span>
                  )}
                </div>
                {expandedSections.actions ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
              </button>

              {expandedSections.actions && (
                <div className="px-5 pb-5 border-t border-gray-100 pt-4">
                  <ActionBuilder actions={actions} onChange={setActions} onEditAction={setEditingAction} />
                </div>
              )}
            </motion.div>

            {/* Bottom Action Bar (mobile-friendly) */}
            <div className="flex gap-3 pt-2 lg:hidden">
              <button
                onClick={() => navigate('/automations')}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors"
              >
                {t('auto_cancel', 'Cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex-1 px-4 py-3 text-white font-semibold text-sm rounded-lg transition-colors disabled:opacity-50"
                style={{ backgroundColor: pc, opacity: 1 }}
                onMouseEnter={(e) => !isSaving && (e.currentTarget.style.opacity = '0.9')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              >
                {isSaving ? t('auto_saving', 'Saving...') : t('auto_save_wf', 'Save Workflow')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowBuilderPage;
